import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { styles } from "../../styles/pages/Stage/StageEditStyles";
import { Modal } from "../../components/ui/modal";
import { CheckCircle } from "lucide-react";
import Loader from "../../components/common/Loader";

const StageEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [stage_name, setStageName] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);

  // Fetch stage data
  useEffect(() => {
    const token = sessionStorage.getItem("access_token");

    if (!token || !id) {
      navigate("/signin");
      setLoading(false);
      return;
    }

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/api/stage/edit`,
        { stage_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((response) => {
        const data = response.data.data[0];
        if (data) {
          setStageName(data.stage_name || "");
        }
      })
      .catch((error) => {
        console.error("Error fetching stage data:", error);
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    if (!stage_name.trim()) {
      setErrors({ stage_name: "The stage name field is required." });
      setSaving(false);
      return;
    }

    const token = sessionStorage.getItem("access_token");

    if (!token || !id) {
      navigate("/signin");
      return;
    }

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/api/stage/edit`,
        { stage_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((stageResponse) => {
        const currentStatus = stageResponse.data?.data[0]?.status || "0";
        const formData = new FormData();
        formData.append("stage_id", id);
        formData.append("stage_name", stage_name);
        formData.append("status", currentStatus);

        return axios.post(
          `${process.env.REACT_APP_API_URL}/api/stage/update`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      })
      .then((response) => {
        if (response.data.status === 200) {
          setSuccessModalOpen(true); // Show success modal
          setTimeout(() => {
            setSuccessModalOpen(false);
            navigate("/stage-list"); // Redirect after modal
          }, 2000); // Delay for better UX
        }
      })
      .catch((error) => {
        console.error("API Error:", error.response?.data || error.message);
        if (error.response?.status === 422) {
          setErrors(error.response.data.errors || {});
        }
      })
      .finally(() => setSaving(false));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStageName(value);
    if (!value.trim()) {
      setErrors({ stage_name: "The stage name field is required." });
    } else {
      setErrors({ stage_name: undefined });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className="flex items-center space-x-4">
          <h2 className={styles.title}>Edit Stage</h2>
        </div>
      </div>
      {loading && (
        <div className="mt-5">
          <Loader />
        </div>
      )}
      {!loading && (
        <div className={styles.formContainer}>
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.inputSection}>
              <div>
                <label className={styles.label}>
                  Stage Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={stage_name}
                  onChange={handleInputChange}
                  className={`${styles.input} ${
                    errors.stage_name
                      ? styles.inputError
                      : "border-gray-300 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10"
                  }`}
                />
                {errors.stage_name && (
                  <span className={styles.errorText}>{errors.stage_name}</span>
                )}
              </div>
            </div>

            <div className={styles.buttonSection}>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => navigate("/stage-list")}
              >
                Cancel
              </button>
            </div>
          </form>

          {/* Success Modal */}
          <Modal
            isOpen={isSuccessModalOpen}
            onClose={() => setSuccessModalOpen(false)}
            className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
          >
            {/* Close Button */}
            <button
              onClick={() => setSuccessModalOpen(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            ></button>

            {/* Success Icon */}
            <div className="flex justify-center">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
                <CheckCircle className="w-6 h-6" />
              </div>
            </div>

            {/* Modal Heading */}
            <h4 className="text-lg font-bold text-gray-800 dark:text-white mt-4">
              Stage Updated Successfully
            </h4>

            {/* Modal Text */}
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              The stage has been updated in the system successfully.
            </p>

            {/* Button */}
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setSuccessModalOpen(false)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                OK
              </button>
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default StageEdit;
